/**
 * Allowlist-based string sanitizer.
 * Validates strings against allowed patterns — no DOM rendering of arbitrary strings.
 * Replaces DOMPurify (per design assumption B4).
 */

const SAFE_LANGUAGE_CODE = /^[a-z]{2}$/;
const SAFE_STYLE_KEY = /^[a-z0-9_]+$/;
const SAFE_VERSION = /^[\d.]+$/;
const SAFE_URL = /^(https?:\/\/[\w.-]+(:[0-9]+)?(\/[\w._~:/?#@!$&'()*+,;=-]*)?|\/[\w._~:/?#@!$&'()*+,;=-]*)$/;

/**
 * Sanitize a language code: only lowercase 2-letter codes allowed.
 */
export function sanitizeLanguageCode(value: string): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim().toLowerCase();
  return SAFE_LANGUAGE_CODE.test(trimmed) ? trimmed : null;
}

/**
 * Sanitize a style key: only lowercase alphanumeric + underscores.
 */
export function sanitizeStyleKey(value: string): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim().toLowerCase();
  return SAFE_STYLE_KEY.test(trimmed) ? trimmed : null;
}

/**
 * Sanitize a version string: only digits and dots.
 */
export function sanitizeVersion(value: string): string | null {
  if (typeof value !== 'string') return null;
  return SAFE_VERSION.test(value.trim()) ? value.trim() : null;
}

/**
 * Sanitize a URL: must be a valid relative or absolute URL from the allowlist pattern.
 */
export function sanitizeUrl(value: string): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return SAFE_URL.test(trimmed) ? trimmed : null;
}

/**
 * Sanitize a numeric value: must be a finite integer within bounds.
 */
export function sanitizeInteger(value: unknown, min: number, max: number): number | null {
  const num = typeof value === 'string' ? parseInt(value, 10) : value;
  if (typeof num !== 'number' || !Number.isFinite(num) || !Number.isInteger(num)) return null;
  if (num < min || num > max) return null;
  return num;
}

/**
 * Escape HTML special characters to prevent XSS.
 * Used only for rendering user-facing strings that come from data.
 */
export function escapeHtml(str: string): string {
  const map: Record<string, string> = {
    '&': '&amp;',
    '<': '&lt;',
    '>': '&gt;',
    '"': '&quot;',
    "'": '&#039;',
  };
  return str.replace(/[&<>"']/g, (c) => map[c] || c);
}
